/**
 * Dialog "Neue Analyse" der Teaser-Analyse.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import type { TeaserDefaultPrompt, TeaserModelOption } from '../../types/teaser';
import { teaserErrorMessage, useCreateTeaserAnalysis } from '../../hooks/useTeaser';

interface Props {
  open: boolean;
  onClose: () => void;
  onStarted: () => void;
  /** undefined, solange die Default-Fragen nicht erfolgreich geladen wurden */
  defaults: TeaserDefaultPrompt[] | undefined;
  defaultsLoading: boolean;
  defaultsError: boolean;
  models: TeaserModelOption[];
  tenantId: number | undefined;
}

const MAX_CUSTOM = 10;

const extractPlaceholders = (s: string): string[] =>
  [...s.matchAll(/\[([\p{L}\p{N}_]+)\]/gu)].map((m) => m[1]);

const NewTeaserAnalysisDialog: React.FC<Props> = ({
  open,
  onClose,
  onStarted,
  defaults: defaultsData,
  defaultsLoading,
  defaultsError,
  models,
  tenantId,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const create = useCreateTeaserAnalysis(tenantId);
  const defaultsReady = defaultsData !== undefined;
  const defaults = useMemo(() => defaultsData ?? [], [defaultsData]);

  const [name, setName] = useState('');
  const [synonym1, setSynonym1] = useState('');
  const [synonym2, setSynonym2] = useState('');
  const [custom, setCustom] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Beim Öffnen zurücksetzen, Modelle aus der Aufstellung vorauswählen
  useEffect(() => {
    if (open) {
      setName('');
      setSynonym1('');
      setSynonym2('');
      setCustom([]);
      setValues({});
      setError(null);
      setSelected(models.filter((m) => m.in_benchmark).map((m) => m.model_code));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Modelle können nachladen, während der Dialog schon offen ist
  useEffect(() => {
    if (open) {
      setSelected((prev) =>
        prev.length > 0 ? prev : models.filter((m) => m.in_benchmark).map((m) => m.model_code)
      );
    }
  }, [open, models]);

  const placeholders = useMemo(
    () =>
      Array.from(
        new Set([
          ...defaults.flatMap((d) => d.placeholders),
          ...custom.flatMap(extractPlaceholders),
        ])
      ),
    [defaults, custom]
  );

  const byProvider = useMemo(() => {
    const groups: Record<string, TeaserModelOption[]> = {};
    models.forEach((m) => {
      (groups[m.provider] ||= []).push(m);
    });
    return groups;
  }, [models]);

  const activeCustom = custom.map((c) => c.trim()).filter(Boolean);
  const questionCount = defaults.length + activeCustom.length;
  const missingValue = placeholders.some((p) => !(values[p] ?? '').trim());
  const canStart =
    name.trim().length >= 2 &&
    !missingValue &&
    selected.length > 0 &&
    defaultsReady &&
    questionCount > 0 &&
    !create.isPending;

  const toggleModel = (code: string) =>
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const handleStart = async () => {
    setError(null);
    const placeholder_values: Record<string, string> = {};
    placeholders.forEach((p) => {
      placeholder_values[p] = (values[p] ?? '').trim();
    });
    try {
      await create.mutateAsync({
        utility_name: name.trim(),
        synonym1: synonym1.trim() || null,
        synonym2: synonym2.trim() || null,
        custom_prompts: activeCustom,
        placeholder_values,
        models: selected,
      });
      onStarted();
      onClose();
    } catch (e) {
      setError(teaserErrorMessage(e, 'Analyse konnte nicht gestartet werden'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="md" fullWidth>
      <DialogTitle>Neue Analyse</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {defaultsLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {defaultsError && !defaultsReady && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Die Default-Fragen konnten nicht geladen werden. Die Analyse kann erst nach
            erfolgreichem Laden gestartet werden.
          </Alert>
        )}
        {defaultsReady && defaults.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Es sind noch keine Default-Fragen angelegt. Legen Sie diese über das Zahnrad an oder
            starten Sie nur mit eigenen Fragen.
          </Alert>
        )}
        <Stack spacing={2}>
          <TextField
            label="Stadtwerk"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 50 }}
            helperText="2 bis 50 Zeichen"
          />
          <Accordion disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Synonyme (optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  label="Synonym 1"
                  value={synonym1}
                  onChange={(e) => setSynonym1(e.target.value)}
                  inputProps={{ maxLength: 50 }}
                />
                <TextField
                  label="Synonym 2"
                  value={synonym2}
                  onChange={(e) => setSynonym2(e.target.value)}
                  inputProps={{ maxLength: 50 }}
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Eigene Fragen ({custom.length}/{MAX_CUSTOM})
            </Typography>
            <Stack spacing={1}>
              {custom.map((c, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label={`Eigene Frage ${i + 1}`}
                    value={c}
                    inputProps={{ maxLength: 1000 }}
                    onChange={(e) =>
                      setCustom((prev) => prev.map((x, j) => (j === i ? e.target.value : x)))
                    }
                  />
                  <IconButton
                    aria-label="Frage entfernen"
                    onClick={() => setCustom((prev) => prev.filter((_, j) => j !== i))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button
              startIcon={<AddIcon />}
              sx={{ mt: 1 }}
              disabled={custom.length >= MAX_CUSTOM}
              onClick={() => setCustom((prev) => [...prev, ''])}
            >
              Eigene Frage
            </Button>
          </Box>

          {placeholders.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Platzhalter
              </Typography>
              <Stack spacing={2}>
                {placeholders.map((p) => (
                  <TextField
                    key={p}
                    required
                    label={p}
                    value={values[p] ?? ''}
                    inputProps={{ maxLength: 100 }}
                    onChange={(e) => setValues((prev) => ({ ...prev, [p]: e.target.value }))}
                  />
                ))}
              </Stack>
            </Box>
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Modelle
            </Typography>
            {Object.entries(byProvider).map(([provider, list]) => (
              <Box key={provider} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {provider}
                </Typography>
                <FormGroup>
                  {list.map((m) => (
                    <FormControlLabel
                      key={m.model_code}
                      control={
                        <Checkbox
                          checked={selected.includes(m.model_code)}
                          onChange={() => toggleModel(m.model_code)}
                        />
                      }
                      label={m.display_name}
                    />
                  ))}
                </FormGroup>
              </Box>
            ))}
          </Box>

          <Alert severity="info">
            {`${questionCount} Fragen × ${selected.length} Modelle = ${
              questionCount * selected.length
            } Abfragen`}
          </Alert>

          {defaults.length > 0 && (
            <Accordion disableGutters variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>{`Mitlaufende Default-Fragen (${defaults.length})`}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack component="ol" spacing={0.5} sx={{ pl: 2, m: 0 }}>
                  {[...defaults]
                    .sort((a, b) => a.position - b.position)
                    .map((d) => (
                      <li key={d.id}>
                        <Typography variant="body2">{d.content}</Typography>
                      </li>
                    ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button variant="contained" onClick={handleStart} disabled={!canStart}>
          Analyse starten
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewTeaserAnalysisDialog;
